import { useState, useEffect, useCallback, useRef } from "react";
import { privateApi } from "../api/axiosConfig";

const POLL_INTERVAL = 60000;

const STATUS_MESSAGES = {
  pending: "در انتظار بررسی فروشنده",
  approved: "درخواست شما تأیید شد! می‌توانید فاکتور را بررسی کنید.",
  rejected: "متأسفانه درخواست شما رد شد.",
  declined: "متأسفانه درخواست شما رد شد.",
  expired: "درخواست شما منقضی شده است.",
  cancelled: "درخواست شما لغو شده است.",
};

const REJECTION_REASONS = {
  "insufficient available stock": "موجودی انبار برای این تعداد کافی نیست.",
  "insufficient stock": "موجودی انبار برای این تعداد کافی نیست.",
  "out of stock": "موجودی این کالا تمام شده است.",
};

export function useInventoryRequestStatus({ variantId, enabled = true }) {
  const [status, setStatus] = useState(null);
  const [quote, setQuote] = useState(null);
  const [notification, setNotification] = useState(null);

  const previousStatusRef = useRef(null);
  const notificationShownRef = useRef(new Set());
  const intervalRef = useRef(null);
  const mountedRef = useRef(false);
  const fetchingRef = useRef(false);

  const fetchRequestStatus = useCallback(async () => {
    if (!variantId || !enabled || fetchingRef.current || !mountedRef.current) return;

    fetchingRef.current = true;
    try {
      const response = await privateApi.get(
        "/api/v1/checkout/inventory-check-requests/list/",
        { params: { page_size: 10 } }
      );

      if (!mountedRef.current) return;

      const requests = response.data.results || response.data || [];
      const variantRequests = requests.filter(r => r.variant?.id === variantId || r.variant === variantId);
      
      if (variantRequests.length > 0) {
        const latestRequest = variantRequests[0];
        const newStatus = latestRequest.status;

        console.debug("[InventoryPoll] variant:", variantId, "status:", newStatus, "prev:", previousStatusRef.current);

        if (previousStatusRef.current && previousStatusRef.current !== newStatus) {
          if (!notificationShownRef.current.has(newStatus)) {
            let message = STATUS_MESSAGES[newStatus] || `وضعیت درخواست به "${newStatus}" تغییر کرد.`;
            
            // If rejected/declined, check for specific rejection reason from backend
            if ((newStatus === "rejected" || newStatus === "declined") && latestRequest) {
              const rejectionReason = latestRequest.rejection_reason 
                || latestRequest.reject_reason 
                || latestRequest.error_message 
                || latestRequest.detail
                || latestRequest.message;
              
              if (rejectionReason) {
                const lowerReason = String(rejectionReason).toLowerCase();
                // Check for known rejection reasons and translate
                for (const [key, translated] of Object.entries(REJECTION_REASONS)) {
                  if (lowerReason.includes(key)) {
                    message = translated;
                    break;
                  }
                }
                // If no known translation, use the backend message as-is (if Persian) or generic
                if (message === STATUS_MESSAGES[newStatus]) {
                  message = /[؀-ۿ]/.test(rejectionReason) 
                    ? rejectionReason 
                    : `${message} دلیل: ${rejectionReason}`;
                }
              }
            }
            
            setNotification({
              status: newStatus,
              message,
              requestId: latestRequest.id,
              quote: latestRequest.quotes?.[0] || null,
            });
            notificationShownRef.current.add(newStatus);
          }
        }

        previousStatusRef.current = newStatus;
        setStatus(newStatus);
        setQuote(latestRequest.quotes?.[0] || null);
      } else {
        console.debug("[InventoryPoll] No requests found for variant:", variantId);
      }
    } catch (err) {
      if (err.response?.status === 429) {
        console.warn("Rate limited, backing off polling");
      } else {
        console.warn("Failed to fetch inventory request status:", err);
      }
    } finally {
      fetchingRef.current = false;
    }
  }, [variantId, enabled]);

  const dismissNotification = useCallback(() => {
    setNotification(null);
  }, []);

  const startPolling = useCallback(() => {
    if (intervalRef.current) return;

    fetchRequestStatus();

    intervalRef.current = setInterval(() => {
      if (mountedRef.current) {
        fetchRequestStatus();
      }
    }, POLL_INTERVAL);
  }, [fetchRequestStatus]);

  const stopPolling = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    previousStatusRef.current = null;
    notificationShownRef.current.clear();

    if (enabled && variantId) {
      startPolling();
    }

    const handleVisibilityChange = () => {
      if (!document.hidden && mountedRef.current && enabled && variantId) {
        fetchRequestStatus();
      }
    };

    const handleFocus = () => {
      if (mountedRef.current && enabled && variantId) {
        fetchRequestStatus();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("focus", handleFocus);

    return () => {
      mountedRef.current = false;
      stopPolling();
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", handleFocus);
    };
  }, [variantId, enabled, startPolling, stopPolling, fetchRequestStatus]);

  return {
    status,
    quote,
    notification,
    dismissNotification,
    isPolling: !!intervalRef.current,
    refetch: fetchRequestStatus,
  };
}

export default useInventoryRequestStatus;